import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { couponService } from '@/services/couponService'
import type { Coupon, CouponUsage } from '@/types/coupon'
import { formatDiscount } from '@/types/coupon'

export const AdminCouponDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [coupon, setCoupon] = useState<Coupon | null>(null)
  const [usages, setUsages] = useState<CouponUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const [couponData, usageData] = await Promise.all([
        couponService.getCoupon(id),
        couponService.getCouponUsages(id),
      ])
      setCoupon(couponData)
      setUsages(usageData)
    } catch (e) {
      setError((e as Error).message || 'Gagal memuat data kupon')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const handleToggleActive = async () => {
    if (!coupon) return
    try {
      if (coupon.isActive) {
        await couponService.deactivateCoupon(coupon.id)
      } else {
        await couponService.activateCoupon(coupon.id)
      }
      await fetchData()
    } catch (e) {
      setError((e as Error).message || 'Gagal mengubah status kupon')
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Tanpa batas'
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-text-muted">Loading...</div>
  }

  if (error || !coupon) {
    return (
      <div className="p-6">
        <PageHeader
          title="Detail Kupon"
          breadcrumbs={[
            { label: 'Manajemen Kupon', to: '/admin/coupons' },
            { label: 'Error' },
          ]}
        />
        <div className="text-red-400">{error || 'Kupon tidak ditemukan'}</div>
      </div>
    )
  }

  const usageProgress = coupon.maxUses != null ? (coupon.currentUses / coupon.maxUses) * 100 : 0

  return (
    <div>
      <PageHeader
        title="Detail Kupon"
        breadcrumbs={[
          { label: 'Manajemen Kupon', to: '/admin/coupons' },
          { label: coupon.code },
        ]}
        badge={coupon.isActive ? 'Aktif' : 'Nonaktif'}
        badgeVariant={coupon.isActive ? 'green' : 'orange'}
      />

      {/* Info Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left Column */}
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
          <h3 className="text-sm font-bold uppercase text-gray-500 dark:text-text-muted mb-4">Informasi Kupon</h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Kode</p>
              <p className="font-mono text-lg font-bold text-gray-900 dark:text-white">{coupon.code}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Nama</p>
              <p className="text-sm text-gray-900 dark:text-white">{coupon.name}</p>
            </div>
            {coupon.description && (
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Deskripsi</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{coupon.description}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Status</p>
              <div className="flex items-center gap-3">
                <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${coupon.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500 dark:text-gray-400'}`}>
                  {coupon.isActive ? 'Aktif' : 'Nonaktif'}
                </span>
                <button
                  onClick={handleToggleActive}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {coupon.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
          <h3 className="text-sm font-bold uppercase text-gray-500 dark:text-text-muted mb-4">Konfigurasi Diskon</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Tipe Diskon</p>
                <p className="text-sm text-gray-900 dark:text-white capitalize">{coupon.discountType === 'percentage' ? 'Persentase' : 'Nominal Tetap'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Nilai Diskon</p>
                <p className="text-sm font-bold text-primary">{formatDiscount(coupon)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Pembelian Minimum</p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {coupon.minimumPurchase > 0 ? `Rp ${coupon.minimumPurchase.toLocaleString('id-ID')}` : 'Tidak ada'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Maks. Per User</p>
                <p className="text-sm text-gray-900 dark:text-white">{coupon.maxUsesPerUser}x</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Maks. Penggunaan</p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {coupon.maxUses != null ? coupon.maxUses : 'Tak terbatas'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Berlaku Dari</p>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(coupon.validFrom)}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">Berlaku Hingga</p>
              <p className="text-sm text-gray-900 dark:text-white">{formatDate(coupon.validUntil)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6 mb-6">
        <h3 className="text-sm font-bold uppercase text-gray-500 dark:text-text-muted mb-4">Ringkasan Penggunaan</h3>
        <div className="flex items-center gap-3 mb-3">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{coupon.currentUses}</p>
          <p className="text-sm text-gray-500 dark:text-text-muted">
            dari {coupon.maxUses != null ? coupon.maxUses : '∞'} penggunaan
          </p>
        </div>
        {coupon.maxUses != null && (
          <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2">
            <div
              className="bg-primary rounded-full h-2 transition-all"
              style={{ width: `${Math.min(usageProgress, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Usage Table */}
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-white/5">
          <h3 className="text-sm font-bold uppercase text-gray-500 dark:text-text-muted">Riwayat Penggunaan</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/5">
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Tanggal</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Pengguna</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Course</th>
                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Diskon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
              {usages.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Belum ada penggunaan</td>
                </tr>
              ) : (
                usages.map((usage) => (
                  <tr key={usage.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">
                      {formatDateTime(usage.usedAt)}
                    </td>
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{usage.userName || '-'}</p>
                        <p className="text-xs text-gray-500 dark:text-text-muted">{usage.userEmail || '-'}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {usage.courseName || '-'}
                    </td>
                    <td className="px-5 py-4 text-sm font-bold text-right text-red-400">
                      Rp {usage.discountAmount.toLocaleString('id-ID')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-3 text-red-300 hover:text-white">Dismiss</button>
        </div>
      )}
    </div>
  )
}

export default AdminCouponDetailPage
